/*
 * Trade Tag Subcomponent
 * Handles displaying, adding, and removing tags for trades
 * Supports both inline table display and bulk add operations
 * Distinguishes between user-added tags and system-generated tags
 */

import React, { useState } from "react";
import axios from "axios";

const TAG_CHAR_LIMIT = 30;

/**
 * TradeTagDisplay Component
 * Displays tags in inline format with edit/add/remove capabilities
 * Used in trade table rows
 */
export function TradeTagDisplay({
    trade_group_id,
    tags = [],
    onTagAdded,
    onTagRemoved,
    onMessage,
    disabled = false
}) {
    const [isEditingTag, setIsEditingTag] = useState(false);
    const [inlineNewTag, setInlineNewTag] = useState("");
    const [inlineTagLoading, setInlineTagLoading] = useState(false);

    // Separate system-generated tags from user tags
    const systemTags = tags.filter(tag => tag.pattern_id != null);
    const userTags = tags.filter(tag => tag.pattern_id == null);

    const handleAddInlineTag = async () => {
        if (!inlineNewTag.trim()) {
            onMessage("Tag cannot be empty", "error");
            return;
        }

        setInlineTagLoading(true);
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/trade-tag-update`,
                {
                    trade_group_id: trade_group_id,
                    tag_notes: inlineNewTag
                },
                { withCredentials: true }
            );

            if (res.data.success) {
                onMessage("✓ Tag added successfully", "success");
                setInlineNewTag("");
                setIsEditingTag(false);

                // Notify parent component of new tag
                if (onTagAdded) {
                    onTagAdded({
                        id: res.data.tag_id,
                        tag_notes: inlineNewTag
                    });
                }
            } else {
                onMessage("Error adding tag: " + (res.data.message || "Unknown error"), "error");
            }
        } catch (err) {
            onMessage("Error adding tag", "error");
            console.error(err);
        } finally {
            setInlineTagLoading(false);
        }
    };

    const handleRemoveTag = async (tag_id) => {
        try {
            const res = await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/trade-tag-remove`,
                {
                    data: { tag_id: tag_id },
                    withCredentials: true
                }
            );

            if (res.data.success) {
                onMessage("✓ Tag removed successfully", "success");

                // Notify parent component of removed tag
                if (onTagRemoved) {
                    onTagRemoved(tag_id);
                }
            } else {
                onMessage("Error removing tag: " + (res.data.message || "Unknown error"), "error");
            }
        } catch (err) {
            onMessage("Error removing tag", "error");
            console.error(err);
        }
    };

    return (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
            {/* Display system-generated tags (read-only) */}
            {systemTags && systemTags.length > 0 && (
                systemTags.map((tag, idx) => (
                    <div
                        key={`system_tag_${tag.id || idx}`}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            background: "#9c27b0",
                            color: "#fff",
                            padding: "2px 8px",
                            borderRadius: 12,
                            fontSize: 12,
                            whiteSpace: "nowrap",
                            fontStyle: "italic",
                            opacity: 0.9,
                            border: "1px solid #7b1fa2"
                        }}
                        title="System-generated tag (pattern detection)"
                    >
                        <span>{tag.tag_notes}</span>
                    </div>
                ))
            )}

            {/* Display user-added tags */}
            {userTags && userTags.length > 0 && (
                userTags.map((tag, idx) => (
                    <div
                        key={`tag_${tag.id || idx}`}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            background: "#1976d2",
                            color: "#fff",
                            padding: "2px 8px",
                            borderRadius: 12,
                            fontSize: 12,
                            whiteSpace: "nowrap"
                        }}
                    >
                        <span>{tag.tag_notes}</span>
                        <span
                            onClick={() => !disabled && handleRemoveTag(tag.id)}
                            style={{
                                cursor: disabled ? "default" : "pointer",
                                fontSize: 14,
                                fontWeight: "bold",
                                marginLeft: 2,
                                opacity: disabled ? 0.5 : 1,
                                transition: "opacity 0.2s"
                            }}
                            title={disabled ? "Cannot remove tags" : "Remove tag"}
                        >
                            ✕
                        </span>
                    </div>
                ))
            )}

            {/* Add or Edit Tag Input */}
            {isEditingTag ? (
                <div style={{ display: "flex", gap: 4, alignItems: "center", minWidth: 250 }}>
                    <input
                        type="text"
                        value={inlineNewTag}
                        onChange={(e) => setInlineNewTag(e.target.value)}
                        placeholder="tag (max 30 chars)"
                        maxLength={TAG_CHAR_LIMIT}
                        autoFocus
                        disabled={disabled}
                        style={{
                            flex: 1,
                            background: "#222",
                            color: "#fff",
                            border: "1px solid #444",
                            borderRadius: 4,
                            padding: 4,
                            fontSize: 12,
                            opacity: disabled ? 0.5 : 1,
                            cursor: disabled ? "not-allowed" : "text"
                        }}
                    />
                    <button
                        onClick={handleAddInlineTag}
                        disabled={inlineTagLoading || disabled}
                        style={{
                            background: "#1976d2",
                            color: "#fff",
                            border: "none",
                            borderRadius: 4,
                            padding: "4px 12px",
                            fontSize: 12,
                            cursor: disabled ? "not-allowed" : "pointer",
                            opacity: inlineTagLoading || disabled ? 0.6 : 1,
                            transition: "opacity 0.2s"
                        }}
                        title={disabled ? "Cannot add tags" : "Save tag"}
                    >
                        {inlineTagLoading ? "..." : "Save"}
                    </button>
                    <button
                        onClick={() => {
                            setIsEditingTag(false);
                            setInlineNewTag("");
                        }}
                        disabled={disabled}
                        style={{
                            background: "#444",
                            color: "#aaa",
                            border: "1px solid #666",
                            borderRadius: 4,
                            padding: "4px 12px",
                            fontSize: 12,
                            cursor: disabled ? "not-allowed" : "pointer",
                            opacity: disabled ? 0.5 : 1
                        }}
                        title={disabled ? "Cannot cancel" : "Cancel"}
                    >
                        Cancel
                    </button>
                </div>
            ) : (
                // Add Tag Button
                tags && tags.length > 0 ? (
                    <button
                        onClick={() => {
                            setIsEditingTag(true);
                            setInlineNewTag("");
                        }}
                        disabled={disabled}
                        style={{
                            background: "transparent",
                            color: "#4caf50",
                            border: "1px solid #4caf50",
                            borderRadius: 12,
                            padding: "2px 8px",
                            fontSize: 12,
                            cursor: disabled ? "not-allowed" : "pointer",
                            fontWeight: 600,
                            opacity: disabled ? 0.5 : 1,
                            transition: "opacity 0.2s"
                        }}
                        title={disabled ? "Cannot add tags" : "Add another tag"}
                    >
                        +Add
                    </button>
                ) : (
                    <button
                        onClick={() => {
                            setIsEditingTag(true);
                            setInlineNewTag("");
                        }}
                        disabled={disabled}
                        style={{
                            background: "#444",
                            color: "#aaa",
                            border: "1px solid #666",
                            borderRadius: 4,
                            padding: "4px 12px",
                            fontSize: 12,
                            cursor: disabled ? "not-allowed" : "pointer",
                            opacity: disabled ? 0.5 : 1,
                            transition: "opacity 0.2s"
                        }}
                        title={disabled ? "Cannot add tags" : "Add a tag"}
                    >
                        Add Tag
                    </button>
                )
            )}
        </div>
    );
}

/**
 * BulkTagSection Component
 * Bulk add tags to multiple selected trades
 * Used above the trade table for batch operations
 */
export function BulkTagSection({
    selectedTradeIds = [],
    trades = [],
    onTagsAdded,
    onMessage,
    disabled = false
}) {
    const [newTag, setNewTag] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const showSection = selectedTradeIds && selectedTradeIds.length > 0;

    const handleBulkAddTag = async () => {
        if (!newTag.trim()) {
            onMessage("Tag cannot be empty", "error");
            return;
        }

        if (selectedTradeIds.length === 0) {
            onMessage("No trades selected", "error");
            return;
        }

        setIsLoading(true);
        try {
            // Create tag update requests for each selected trade
            const updatePromises = selectedTradeIds.map(tradeGroupId =>
                axios.post(
                    `${process.env.REACT_APP_API_URL}/api/trade-tag-update`,
                    {
                        trade_group_id: tradeGroupId,
                        tag_notes: newTag
                    },
                    { withCredentials: true }
                )
            );

            const results = await Promise.all(updatePromises);

            // Check if all requests were successful
            const allSuccessful = results.every(res => res.data.success);

            if (allSuccessful) {
                onMessage(`✓ Tags added to ${selectedTradeIds.length} trades`, "success");

                // Notify parent component
                if (onTagsAdded) {
                    onTagsAdded({
                        tag_notes: newTag,
                        trade_ids: selectedTradeIds
                    });
                }

                setNewTag("");
            } else {
                onMessage("Error adding some tags", "error");
            }
        } catch (err) {
            onMessage("Error adding tags: " + (err.response?.data?.message || "Unknown error"), "error");
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    if (!showSection) {
        return null;
    }

    return (
        <div
            style={{
                marginTop: 16,
                padding: 16,
                background: "#2d3540",
                borderRadius: 8,
                border: "2px solid #1976d2"
            }}
        >
            <div style={{ marginBottom: 12, color: "#bbb", fontSize: 13 }}>
                <strong>{selectedTradeIds.length}</strong> trades selected
            </div>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Enter tag (max 30 chars)"
                    maxLength={TAG_CHAR_LIMIT}
                    disabled={disabled || isLoading}
                    style={{
                        flex: 1,
                        background: "#222",
                        color: "#fff",
                        border: "1px solid #444",
                        borderRadius: 4,
                        padding: 8,
                        fontSize: 15,
                        opacity: disabled || isLoading ? 0.6 : 1,
                        cursor: disabled || isLoading ? "not-allowed" : "text"
                    }}
                />
                <button
                    onClick={handleBulkAddTag}
                    disabled={disabled || isLoading || !newTag.trim()}
                    style={{
                        background: "#1976d2",
                        color: "#fff",
                        border: "none",
                        borderRadius: 4,
                        padding: "8px 24px",
                        fontSize: 15,
                        cursor: disabled || isLoading ? "not-allowed" : "pointer",
                        fontWeight: 600,
                        opacity: disabled || isLoading || !newTag.trim() ? 0.6 : 1,
                        transition: "opacity 0.2s"
                    }}
                    title={
                        !newTag.trim()
                            ? "Enter a tag name"
                            : disabled || isLoading
                                ? "Operation in progress..."
                                : "Add tag to all selected trades"
                    }
                >
                    {isLoading ? "Adding..." : "Add Tag"}
                </button>
            </div>
        </div>
    );
}

/**
 * TagViewer Component
 * Lightweight component for displaying tags only (read-only)
 * Used when tag management is disabled
 * Shows both system-generated and user-added tags
 */
export function TagViewer({ tags = [] }) {
    if (!tags || tags.length === 0) {
        return (
            <span style={{ color: "#999", fontSize: 12 }}>
                No tags
            </span>
        );
    }

    // Separate system-generated tags from user tags
    const systemTags = tags.filter(tag => tag.pattern_id != null);
    const userTags = tags.filter(tag => tag.pattern_id == null);

    return (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
            {/* Display system-generated tags */}
            {systemTags && systemTags.length > 0 && (
                systemTags.map((tag, idx) => (
                    <div
                        key={`system_tag_${tag.id || idx}`}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            background: "#9c27b0",
                            color: "#fff",
                            padding: "2px 8px",
                            borderRadius: 12,
                            fontSize: 12,
                            whiteSpace: "nowrap",
                            fontStyle: "italic",
                            opacity: 0.9,
                            border: "1px solid #7b1fa2"
                        }}
                        title="System-generated tag (pattern detection)"
                    >
                        {tag.tag_notes}
                    </div>
                ))
            )}

            {/* Display user-added tags */}
            {userTags && userTags.length > 0 && (
                userTags.map((tag, idx) => (
                    <div
                        key={`tag_${tag.id || idx}`}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            background: "#1976d2",
                            color: "#fff",
                            padding: "2px 8px",
                            borderRadius: 12,
                            fontSize: 12,
                            whiteSpace: "nowrap"
                        }}
                    >
                        {tag.tag_notes}
                    </div>
                ))
            )}
        </div>
    );
}

export default TradeTagDisplay;